import { AskRequest, CreateAskRequestGroup } from '@hone-automation/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast, TOAST_ERROR, TOAST_SUCCESS, FIVE_SECONDS, TEN_SECONDS } from 'lib/utils';
import {
  makeRemotePostQueueGroupRequest,
  makeRemotePostQueueGroupAssign,
  makeRemotePostQueueGroupUnassign,
  makeRemotePatchQueueRequestPriority,
} from 'main/factories/usecases';

interface UpdateParentIdData {
  requestId: string;
  parentId: string | null;
}

interface BulkGroupData {
  groupId: string;
  childRequestIds: string[];
}

/**
 * Hook for bulk assigning requests to a group
 * POST /queue/requests/group/{groupId}/assign
 */

/**
 * Hook for creating a new group (AskRequest with type='Group')
 * Groups are created via POST /queue/requests/group endpoint
 */
export const useCreateGroupMutation = (locationId: string) => {
  const queryClient = useQueryClient();
  const createRequest = makeRemotePostQueueGroupRequest();

  return useMutation({
    mutationFn: async (data: CreateAskRequestGroup) => {
      return createRequest.post(data as any);
    },
    onSuccess: async data => {
      const request = data as AskRequest;
      // Invalidate all request queries since groups may be shown in different views
      await queryClient.invalidateQueries({ queryKey: ['queueRequests'] });
      await queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      await queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary', locationId] });
      showToast('Group created successfully', TOAST_SUCCESS, FIVE_SECONDS);
      return request;
    },
    onError: (e: Error) => {
      showToast(e.message || 'Error creating group', TOAST_ERROR, TEN_SECONDS);
    },
  });
};

/**
 * Hook for updating parentId (adding/removing from group)
 * This uses the PATCH /queue/requests/:id endpoint with parentId field
 */
export const useUpdateParentIdMutation = (
  locationId: string,
  onSuccessCallback?: (request: AskRequest | undefined) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateParentIdData) => {
      const service = makeRemotePatchQueueRequestPriority(data.requestId);
      const patchPayload = {
        parentId: data.parentId,
      };
      const result = await service.patch(patchPayload as any);
      return result;
    },
    onSuccess: result => {
      // Invalidate all request queries to refresh groups and child requests (non-blocking)
      queryClient.invalidateQueries({ queryKey: ['queueRequests'] });
      queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary', locationId] });

      if (onSuccessCallback) {
        onSuccessCallback(result as AskRequest);
      }
    },
    onError: (e: Error) => {
      showToast(e.message || 'Error updating group membership', TOAST_ERROR, TEN_SECONDS);
    },
  });
};

export const useBulkAssignToGroupMutation = (locationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkGroupData) => {
      const service = makeRemotePostQueueGroupAssign(data.groupId);
      return service.post({ childRequestIds: data.childRequestIds } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueRequests'] });
      queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary', locationId] });
    },
    onError: (e: Error) => {
      showToast(e.message || 'Error assigning to group', TOAST_ERROR, TEN_SECONDS);
    },
  });
};

/**
 * Hook for bulk unassigning requests from a group
 * POST /queue/requests/group/{groupId}/unassign
 */
export const useBulkUnassignFromGroupMutation = (locationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkGroupData) => {
      const service = makeRemotePostQueueGroupUnassign(data.groupId);
      return service.post({ childRequestIds: data.childRequestIds } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueRequests'] });
      queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary', locationId] });
    },
    onError: (e: Error) => {
      showToast(e.message || 'Error removing from group', TOAST_ERROR, TEN_SECONDS);
    },
  });
};
